import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import './ItemTooltip.css';
import { useMercenary } from '../../contexts/MercenaryContext';
import { getItemImageSrc, handleImgError } from '../../utils/itemUtils';

interface SocketedRuneInfo {
  runeSlotIndex: number;
  rune: {
    runeColor: string;
    name: string;
    enchantModifiers?: string;
  };
}

interface TooltipProps {
  itemId?: number;
  item?: any;
  children: React.ReactNode;
  align?: 'center' | 'left' | 'right';
  className?: string;
  style?: React.CSSProperties;
  socketedRunes?: SocketedRuneInfo[];
  boundNote?: string;
}

const Tooltip: React.FC<TooltipProps> = ({ itemId, item: propItem, children, align = 'center', className, style, socketedRunes, boundNote }) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [item, setItem] = useState<any>(propItem || null);
  const [loading, setLoading] = useState(false);
  const { mercenary } = useMercenary();

  useEffect(() => {
    if (propItem) {
      setItem((prev: any) => {
        // Preserve enriched data (professionName etc.) if this is the same item
        if (prev && prev.id === propItem.id && prev.professionName !== undefined) return prev;
        return propItem;
      });
    }
  }, [propItem]);

  const getDefaultDimensions = (category: string | undefined) => {
    switch (category) {
      case 'Weapon':
        return { width: 320, height: 340 };
      case 'Shield':
      case 'Armor':
      case 'Accessory':
      case 'Potion':
        return { width: 320, height: 220 };
      case 'Material':
      case 'Knowledge':
        return { width: 320, height: 180 };
      case 'Rune':
        return { width: 280, height: 160 };
      default:
        return { width: 320, height: 200 };
    }
  };

  const [tooltipDimensions, setTooltipDimensions] = useState(getDefaultDimensions(undefined));
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  const handleMouseOver = (e: React.MouseEvent) => {
    setVisible(true);
    handleMouseMove(e);
  };

  const handleMouseOut = () => {
    setVisible(false);
    setItem(null);
    setLoading(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    // visible check removed to allow initial positioning on hover
    
    // Use actual dimensions if available, otherwise fallback to estimated
    const width = tooltipRef.current?.offsetWidth || tooltipDimensions.width;
    const height = tooltipRef.current?.offsetHeight || tooltipDimensions.height;
    
    let x = e.clientX + 15;
    let y = e.clientY + 15;

    // Check right edge
    if (x + width > window.innerWidth) {
      x = e.clientX - width - 15;
    }

    // Check bottom edge
    if (y + height > window.innerHeight) {
      y = e.clientY - height - 15;
    }
    
    // Check left edge (if flipped left and goes off screen)
    if (x < 0) {
      x = 10; // Padding from left
    }
    
    // Check top edge (if flipped up and goes off screen)
    if (y < 0) {
      y = 10; // Padding from top
    }

    setPosition({ x, y });
  };

  const fetchItemData = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const params: Record<string, any> = {};
      if (mercenary?.id) params.mercenaryId = mercenary.id;
      const response = await axios.get(`/api/item/${itemId}`, { params });
      const fetchedItem = response.data;
      setItem(fetchedItem);
      setLoading(false);

      const defaultDimensions = getDefaultDimensions(fetchedItem.itemCategory);
      const extraHeight = fetchedItem.description?.length > 100 ? 50 : 0;
      setTooltipDimensions({
        width: defaultDimensions.width,
        height: defaultDimensions.height + extraHeight,
      });
    } catch (error) {
      console.error('Failed to fetch item data:', error);
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    if (propItem) {
      setItem((prev: any) => {
        // Preserve enriched data (professionName etc.) if this is the same item
        if (prev && prev.id === propItem.id && prev.professionName !== undefined) return prev;
        return propItem;
      });
      return;
    }
    if (visible && itemId && (!item || item.id !== itemId)) {
      const debounceFetch = setTimeout(fetchItemData, 10);
      return () => clearTimeout(debounceFetch);
    }
  }, [itemId, visible, item, fetchItemData, propItem]);

  // Enrich Knowledge items passed as propItem — they lack professionName until fetched from the API.
  useEffect(() => {
    if (!visible || !item || item.itemCategory !== 'Knowledge' || item.professionName !== undefined) return;
    const params: Record<string, any> = {};
    if (mercenary?.id) params.mercenaryId = mercenary.id;
    axios.get(`/api/item/${item.id}`, { params })
      .then(res => setItem(res.data))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, item?.id, item?.itemCategory, item?.professionName]);

  const meetsRequirements = () => {
    const race = mercenary?.race || '';
    const title = mercenary?.title || '';

    return {
      level: item?.requiredLevel ? (mercenary?.level ?? 0) >= item.requiredLevel : true,
      strength: item?.strengthRequirement ? (mercenary?.strength ?? 0) >= item.strengthRequirement : true,
      offhandStrength: item?.strengthRequirement ? (mercenary?.strength ?? 0) >= item.strengthRequirement * 2 : true,
      race:
        !item?.itemRace ||
        item.itemRace === 'All' ||
        item.itemRace === race ||
        (item.itemRace === 'Red Blood' && ['Elf', 'Dwarf', 'Human'].includes(race)) ||
        (item.itemRace === 'Black Blood' && ['Orc', 'Goblin', 'Troll'].includes(race)),
      title: (() => {
        if (!item?.itemTitle || item.itemTitle === 'All' || item.itemTitle === 'Normal') return true;
        // Reputation titles: exact title match required
        if (item.itemTitle === 'Dishonorable' || item.itemTitle === 'Reputable') return title === item.itemTitle;
        // Combat rank hierarchy: Unknown(0) < Pit Fighter(1) < Gladiator(2) < Veteran(3) < Legend(4)
        const TITLE_RANK: Record<string, number> = { Unknown: 0, 'Pit Fighter': 1, Gladiator: 2, Veteran: 3, Legend: 4 };
        const required = TITLE_RANK[item.itemTitle] ?? 0;
        // When holding a reputation title (Dishonorable/Reputable), derive combat rank from level
        const getEffectiveCombatRank = (): number => {
          if (title === 'Legend') return 4;
          if (TITLE_RANK[title] !== undefined) return TITLE_RANK[title];
          const lv = mercenary?.level ?? 0;
          if (lv <= 9)  return 0;
          if (lv <= 15) return 1;
          if (lv <= 25) return 2;
          return 3;
        };
        return getEffectiveCombatRank() >= required;
      })(),
    };
  };

  const renderRequirements = () => {
    if (!item) return null;
    const requirements = meetsRequirements();
    const showRace = item.itemRace && item.itemRace !== 'All';
    const showTitle = item.itemTitle && item.itemTitle !== 'Normal';
    return (
      <div className="tooltip-meta">
        <span className={`req-badge ${requirements.level ? 'req-pass' : 'req-fail'}`}>
          {item.itemCategory === 'Knowledge' && item.professionName ? `${item.professionName} Lvl` : 'Level'} {item.requiredLevel ?? 'N/A'}
        </span>
        {showRace && (
          <span className={`req-badge ${requirements.race ? 'req-pass' : 'req-fail'}`}>
            Race {item.itemRace}
          </span>
        )}
        {showTitle && (
          <span className={`req-badge ${requirements.title ? 'req-pass' : 'req-fail'}`}>
            Title {item.itemTitle}
          </span>
        )}
      </div>
    );
  };

  const renderHeader = (extraChips?: React.ReactNode, showRequirements = true) => {
    if (!item) return null;
    return (
      <div className="tooltip-head">
        <div className="tooltip-titleBlock">
          <div className="tooltip-name">{item.name}</div>
          <div className="tooltip-meta">
            {item.itemCategory && <span className="tooltip-chip">{item.itemCategory}</span>}
            {extraChips}
          </div>
          {showRequirements && renderRequirements()}
        </div>
        <img
          src={getItemImageSrc(item)}
          alt={item.name}
          className="tooltip-portrait"
          onError={handleImgError}
        />
      </div>
    );
  };

  const renderBonuses = () => {
    if (!item?.bonuses) return null;
    return item.bonuses.split(',').map((bonus: string, index: number) => (
      <p key={index}>{bonus.trim()}</p>
    ));
  };

  const RUNE_COLOR_CSS: Record<string, string> = {
    green: '#4CAF50', orange: '#FF9800', blue: '#2196F3', red: '#f44336',
    aqua: '#00BCD4', yellow: '#FFEB3B', gray: '#9E9E9E', purple: '#9C27B0',
  };

  const renderRuneSlots = () => {
    if (!item?.runeSlots?.length) return null;
    const runeSlots: string[] = item.runeSlots;
    return (
      <div className="tooltip-rune-sockets">
        <span className="tooltip-rune-sockets-label">Rune Slots</span>
        <div className="tooltip-socket-circles">
          {runeSlots.map((color: string, idx: number) => {
            const socketed = socketedRunes?.find(sr => sr.runeSlotIndex === idx);
            if (socketed) {
              return (
                <span
                  key={idx}
                  className="tooltip-socket-filled"
                  style={{ background: RUNE_COLOR_CSS[socketed.rune.runeColor] || '#555' }}
                  title={`${socketed.rune.name} (${socketed.rune.runeColor})`}
                />
              );
            }
            return (
              <span
                key={idx}
                className="tooltip-socket-empty"
                style={{ borderColor: RUNE_COLOR_CSS[color] || '#666' }}
                title={`${color} rune slot (empty)`}
              />
            );
          })}
        </div>
      </div>
    );
  };

  const renderSocketedRuneBonuses = () => {
    if (!socketedRunes?.length) return null;
    return (
      <div className="tooltip-socketed-bonuses">
        <div className="tooltip-socketed-header">Socketed Runes</div>
        {socketedRunes.map((sr, i) => (
          <div key={i} className="tooltip-socketed-rune">
            <span
              className="tooltip-socketed-name"
              style={{ color: RUNE_COLOR_CSS[sr.rune.runeColor] || '#aaa' }}
            >
              {sr.rune.name}
            </span>
            {sr.rune.enchantModifiers && sr.rune.enchantModifiers.split(',').map((b: string, bi: number) => (
              <p key={bi} className="tooltip-socketed-bonus">{b.trim()}</p>
            ))}
          </div>
        ))}
      </div>
    );
  };

  const renderItemDetails = () => {
    if (!item) return <p>Loading...</p>;

    const formatSlot = (slot: string) => {
      if (slot === 'oneHand') return 'One-Handed';
      if (slot === 'twoHand') return 'Two-Handed';
      return slot;
    };

    const meetsWeaponSkillRecommendation = item?.skillRecommendation
      ? (mercenary?.weaponSkills?.[item.weaponType?.toLowerCase() as keyof typeof mercenary.weaponSkills] ?? 0) >=
        item.skillRecommendation
      : true;

    const meetsShieldSkillRecommendation = item?.skillRecommendation
      ? (mercenary?.weaponSkills?.['shield'] ?? 0) >= item.skillRecommendation
      : true;

    switch (item.itemCategory) {
      case 'Weapon': {
        const extraChips = (
          <>
            {item.weaponType && <span className="tooltip-chip subtle">{item.weaponType}</span>}
            {item.slot && <span className="tooltip-chip subtle">{formatSlot(item.slot)}</span>}
          </>
        );

        return (
          <div className="tooltip-content">
            {renderHeader(extraChips)}
            <div className="tooltip-divider" />
            <div className="tooltip-grid">
              <div className="stat-line"><span className="label">Damage</span><span className="value">{item.minDamage ?? 'N/A'} - {item.maxDamage ?? 'N/A'} ({item.dmgCap ?? 'N/A'})</span></div>
              <div className="stat-line"><span className="label">Crit %</span><span className="value">{item.critChance}%</span></div>
              <div className="stat-line"><span className="label">Crit Dmg</span><span className="value">{item.critDamage}%</span></div>
              <div className="stat-line"><span className="label">Durability</span><span className="value">{item.breakValue ?? 'N/A'}</span></div>
              <div className="stat-line"><span className="label">Off-Hand</span><span className="value">{item.canEquipInOffHand ? 'Yes' : 'No'}</span></div>
              <div className="stat-line"><span className="label">Actions</span><span className="value">{item.actionsPerRound ?? 'N/A'}</span></div>
              {item.skillRecommendation && (
                <div className="stat-line"><span className="label">Skill Rec.</span><span className="value" style={{ color: meetsWeaponSkillRecommendation ? '#c0f1b3' : '#ff9b9b' }}>{item.skillRecommendation}</span></div>
              )}
              {item.strengthRequirement != null && (
                <div className="stat-line"><span className="label">Str. Req.</span><span className="value" style={{ color: meetsRequirements().strength ? '#c0f1b3' : '#ff9b9b' }}>{item.strengthRequirement}{item.canEquipInOffHand ? <span style={{ color: meetsRequirements().offhandStrength ? '#c0f1b3' : '#ff9b9b' }}> ({item.strengthRequirement * 2})</span> : ''}</span></div>
              )}
              {item.weaponType === 'Staff' && (
                <>
                  <div className="stat-line"><span className="label">Momentum %</span><span className="value">{item.momentumChance ? (item.momentumChance * 100).toFixed(0) + '%' : 'N/A'}</span></div>
                  <div className="stat-line"><span className="label">Momentum Mult.</span><span className="value">{item.momentumMultiplier ? (item.momentumMultiplier * 100).toFixed(0) + '%' : 'N/A'}</span></div>
                </>
              )}
              {item.weight != null && item.weight > 0 && (
                <div className="stat-line"><span className="label">Weight</span><span className="value">{item.weight}</span></div>
              )}
            </div>
            <div className="tooltip-divider" />
            {renderBonuses()}
            {renderRuneSlots()}
            {renderSocketedRuneBonuses()}
          </div>
        );
      }
      case 'Shield': {
        const extraChips = item.slot ? <span className="tooltip-chip subtle">{formatSlot(item.slot)}</span> : null;
        return (
          <div className="tooltip-content">
            {renderHeader(extraChips)}
            <div className="tooltip-divider" />
            <div className="tooltip-grid">
              <div className="stat-line"><span className="label">Block</span><span className="value">{item.blockValue ?? 'N/A'}</span></div>
              <div className="stat-line"><span className="label">Durability</span><span className="value">{item.breakValue ?? 'N/A'}</span></div>
              <div className="stat-line"><span className="label">Actions</span><span className="value">{item.actionsPerRound ?? 'N/A'}</span></div>
              {item.skillRecommendation > 0 && (
                <div className="stat-line"><span className="label">Skill Rec.</span><span className="value" style={{ color: meetsShieldSkillRecommendation ? '#c0f1b3' : '#ff9b9b' }}>{item.skillRecommendation}</span></div>
              )}
              {item.strengthRequirement > 0 && (
                <div className="stat-line"><span className="label">Str. Req.</span><span className="value" style={{ color: meetsRequirements().strength ? '#c0f1b3' : '#ff9b9b' }}>{item.strengthRequirement}</span></div>
              )}
              {item.weight != null && item.weight > 0 && (
                <div className="stat-line"><span className="label">Weight</span><span className="value">{item.weight}</span></div>
              )}
            </div>
            <div className="tooltip-divider" />
            {renderBonuses()}
            {renderRuneSlots()}
            {renderSocketedRuneBonuses()}
          </div>
        );
      }
      case 'Armor': {
        const extraChips = item.armorType ? <span className="tooltip-chip subtle">{item.armorType}</span> : null;
        return (
          <div className="tooltip-content">
            {renderHeader(extraChips)}
            <div className="tooltip-divider" />
            {item.absorption ? (
              <p><strong>Absorption:</strong> {item.absorption}</p>
            ) : item.absorptionPercentage ? (
              <p>
                <strong>Absorption:</strong> {(item.absorptionPercentage * 100).toFixed(0)}%
                {item.minAbsorption !== null && item.maxAbsorption !== null && (
                  <> (Min: {item.minAbsorption}, Max: {item.maxAbsorption})</>
                )}
              </p>
            ) : (
              <p><strong>Absorption:</strong> N/A</p>
            )}
            {item.weight != null && item.weight > 0 && (
              <div className="tooltip-grid"><div className="stat-line"><span className="label">Weight</span><span className="value">{item.weight}</span></div></div>
            )}
            {renderBonuses()}
            {renderRuneSlots()}
            {renderSocketedRuneBonuses()}
          </div>
        );
      }
      case 'Accessory': {
        const extraChips = item.accessoryType ? <span className="tooltip-chip subtle">{item.accessoryType}</span> : null;
        return (
          <div className="tooltip-content">
            {renderHeader(extraChips)}
            <div className="tooltip-divider" />
            {item.weight != null && item.weight > 0 && (
              <div className="tooltip-grid"><div className="stat-line"><span className="label">Weight</span><span className="value">{item.weight}</span></div></div>
            )}
            {renderBonuses()}
            {renderRuneSlots()}
            {renderSocketedRuneBonuses()}
          </div>
        );
      }
      case 'Potion': {
        return (
          <div className="tooltip-content">
            {renderHeader(null)}
            <div className="tooltip-divider" />
            <div className="tooltip-section">
              <p><strong>Duration:</strong> {item.duration ?? 'N/A'} minutes</p>
              <p><strong>Effect:</strong> {item.effectDescription ?? item.potionEffect ?? 'N/A'}</p>
            </div>
          </div>
        );
      }
      case 'Material': {
        const extraChips = item.materialType ? <span className="tooltip-chip subtle">{item.materialType}</span> : null;
        return (
          <div className="tooltip-content">
            {renderHeader(extraChips)}
            <div className="tooltip-divider" />
            <p><strong>Description:</strong> {item.description ?? 'N/A'}</p>
          </div>
        );
      }
      case 'Knowledge': {
        const extraChips = item.professionName
          ? <span className="tooltip-chip subtle">{item.professionName}</span>
          : item.knowledgeType ? <span className="tooltip-chip subtle">{item.knowledgeType}</span> : null;
        const isBlueprintOrScroll = item.name && (item.name.startsWith('Blueprint:') || item.name.startsWith('Scroll:') || item.name.startsWith('Book:') || item.name.startsWith('Tome:'));
        return (
          <div className="tooltip-content">
            {renderHeader(extraChips)}
            <div className="tooltip-divider" />
            <p>{item.description}</p>
            {isBlueprintOrScroll && (
              <p style={{ color: '#aaa', fontSize: '0.85rem', marginTop: '8px', fontStyle: 'italic' }}>
                Use to add this blueprint to your study list.
              </p>
            )}
            {item.alreadyKnown && (
              <p style={{ color: '#f0c040', fontSize: '0.85rem', marginTop: '6px', fontWeight: 600 }}>
                ★ Already in your study list
              </p>
            )}
          </div>
        );
      }
      case 'Rune': {
        const TIER_LABELS: Record<number, string> = { 1: 'Flawed', 2: 'Fine', 3: 'Excellent', 4: 'Perfect', 5: 'Mythical' };
        const tierLabel = item.runeTier ? TIER_LABELS[item.runeTier] : null;
        const colorStyle = item.runeColor ? { color: RUNE_COLOR_CSS[item.runeColor] || 'inherit' } : {};
        const extraChips = (
          <>
            {item.runeColor && <span className="tooltip-chip subtle" style={colorStyle}>{item.runeColor}</span>}
            {tierLabel && <span className="tooltip-chip subtle">{tierLabel}</span>}
          </>
        );
        return (
          <div className="tooltip-content">
            {renderHeader(extraChips, false)}
            <div className="tooltip-divider" />
            {item.enchantModifiers && item.enchantModifiers.split(',').map((b: string, i: number) => (
              <p key={i} style={{ color: '#c0f1b3' }}>{b.trim()}</p>
            ))}
          </div>
        );
      }
      default:
        return <p>No details available for this item category.</p>;
    }
  };

  return (
    <>
      <div 
        onMouseOver={handleMouseOver} 
        onMouseMove={handleMouseMove} 
        onMouseOut={handleMouseOut} 
        className={`tooltip-wrapper ${className || ''}`}
        style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: align === 'left' ? 'flex-start' : align === 'right' ? 'flex-end' : 'center',
          height: '100%',
          ...style
        }}
      >
        {children}
      </div>
      {visible && ReactDOM.createPortal(
        <div
          ref={tooltipRef}
          className="tooltip"
          style={{
            top: position.y,
            left: position.x,
            position: 'fixed',
            zIndex: 9999,
            width: tooltipDimensions.width,
          }}
        >
          {boundNote && (
            <div className="tooltip-bound-note">{boundNote}</div>
          )}
          <div className="tooltip-body">
            {loading ? <p>Loading...</p> : renderItemDetails()}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default Tooltip;
